import React, { useEffect, useState } from "react";
import axios from "axios";
import { Plus, Pencil, Trash2, Star, Loader2, Check, X, Tag, Sparkles } from "lucide-react";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./components/ui/dialog";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export function useOffers(enabled = true) {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(false);

  const refresh = async () => {
    if (!enabled) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/offers`);
      setOffers(data);
    } catch (e) {
      if (e?.response?.status !== 402) {
        toast.error("Couldn't load offers");
      }
      setOffers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [enabled]);

  return { offers, loading, refresh };
}

export function OfferManager({ billing, onUpgrade, onTrial, trialEligible }) {
  const { offers, loading, refresh } = useOffers(true);
  const [editing, setEditing] = useState(null); // null | "new" | offer_id
  const isPaid = billing?.plan !== "free";
  const limit = billing?.offer_limit ?? 0;
  const atLimit = isPaid && offers.length >= limit;

  return (
    <section
      className="relative overflow-hidden rounded-2xl border border-white/[0.08] bg-white/[0.02] backdrop-blur-xl p-6 sm:p-8 animate-fade-up"
      data-testid="offer-manager"
    >
      <div className="flex items-start justify-between gap-4 mb-5 flex-wrap">
        <div className="flex items-start gap-4">
          <div className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-red-500/10 text-red-400 border border-red-500/25">
            <Tag className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-xl sm:text-2xl font-semibold text-white tracking-tight">Your offers</h2>
            <p className="text-sm text-zinc-400 mt-1 max-w-2xl leading-relaxed">
              Each offer is a landing page you promote on YouTube. Short links and UTM links can target any offer. Your default offer is used in the videos table by default.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {isPaid && (
            <div className="text-xs text-zinc-400" data-testid="offer-limit-counter">
              <span className="text-white font-medium">{offers.length}</span>
              <span className="text-zinc-500"> / {limit} used</span>
            </div>
          )}
          <Button
            onClick={() => isPaid ? setEditing("new") : onUpgrade?.()}
            disabled={atLimit}
            className="bg-red-600 hover:bg-red-500 text-white border border-red-400/30 shadow-[0_0_15px_rgba(255,0,0,0.3)] disabled:opacity-40 disabled:shadow-none"
            data-testid="offer-new-btn"
          >
            <Plus className="w-4 h-4 mr-1.5" />
            {atLimit ? "Limit reached — upgrade to Pro" : "New offer"}
          </Button>
        </div>
      </div>

      {!isPaid ? (
        <div className="rounded-xl border border-dashed border-red-500/30 bg-red-500/[0.04] p-7 text-center" data-testid="offers-locked">
          <Sparkles className="w-6 h-6 text-red-400 mx-auto mb-2" />
          <div className="text-white font-medium mb-1">
            {trialEligible ? "Start your 14-day free trial to unlock offers" : "Offers are a paid feature"}
          </div>
          <div className="text-sm text-zinc-400 mb-4">
            {trialEligible
              ? "Start your 14-day free trial — $0 today. Starter $29/mo or Pro $79/mo after. Cancel anytime."
              : "Subscribe to Starter ($29/mo, fair-use caps) or Pro ($79/mo, unlimited AI)."}
          </div>
          {trialEligible && onTrial ? (
            <div className="flex flex-col items-center gap-2">
              <Button
                onClick={onTrial}
                className="bg-red-600 hover:bg-red-500 text-white border border-red-400/30"
                data-testid="offers-locked-trial-btn"
              >
                Start 14-day free trial
              </Button>
              <button
                type="button"
                onClick={onUpgrade}
                className="text-xs text-zinc-500 hover:text-zinc-300 underline underline-offset-2 transition-colors"
                data-testid="offers-locked-plans-link"
              >
                or see plans
              </button>
            </div>
          ) : (
            <Button onClick={onUpgrade} className="bg-red-600 hover:bg-red-500 text-white border border-red-400/30">See plans</Button>
          )}
        </div>
      ) : loading && offers.length === 0 ? (
        <div className="py-10 text-center text-zinc-500"><Loader2 className="w-5 h-5 inline animate-spin mr-2" /> Loading offers…</div>
      ) : offers.length === 0 ? (
        <div className="rounded-xl border border-dashed border-white/15 p-8 text-center text-zinc-400">
          <Tag className="w-7 h-7 text-zinc-600 mx-auto mb-2" />
          <div className="text-white font-medium mb-1">No offers yet</div>
          <div className="text-sm">Add your first product or landing page to start tracking.</div>
        </div>
      ) : (
        <div className="grid gap-3" data-testid="offer-list">
          {offers.map((o) => (
            <OfferRow
              key={o.offer_id}
              offer={o}
              onEdit={() => setEditing(o.offer_id)}
              onRefresh={refresh}
            />
          ))}
        </div>
      )}

      {editing && (
        <OfferDialog
          mode={editing === "new" ? "new" : "edit"}
          offer={editing !== "new" ? offers.find((o) => o.offer_id === editing) : null}
          open={!!editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); refresh(); }}
        />
      )}
    </section>
  );
}

function OfferRow({ offer, onEdit, onRefresh }) {
  const [busy, setBusy] = useState(false);

  const makeDefault = async () => {
    setBusy(true);
    try {
      await axios.patch(`${API}/offers/${offer.offer_id}`, { is_default: true });
      toast.success("Default offer updated");
      onRefresh();
    } catch {
      toast.error("Couldn't update");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete offer "${offer.name}"?`)) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/offers/${offer.offer_id}`);
      toast.success("Offer deleted");
      onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="flex items-center gap-4 p-4 rounded-xl bg-black/30 border border-white/[0.06] hover:bg-black/40 transition-colors"
      data-testid={`offer-row-${offer.offer_id}`}
    >
      <div className="flex-shrink-0">
        <button
          type="button"
          onClick={makeDefault}
          disabled={busy || offer.is_default}
          className={`w-9 h-9 rounded-xl flex items-center justify-center transition-colors ${
            offer.is_default
              ? "bg-red-500/15 text-red-400 border border-red-500/30"
              : "bg-white/[0.04] text-zinc-500 border border-white/10 hover:text-white hover:bg-white/[0.08]"
          }`}
          title={offer.is_default ? "Default offer" : "Set as default"}
          data-testid={`offer-default-${offer.offer_id}`}
        >
          <Star className={`w-4 h-4 ${offer.is_default ? "fill-current" : ""}`} />
        </button>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <div className="text-white font-medium truncate" data-testid={`offer-name-${offer.offer_id}`}>{offer.name}</div>
          {offer.is_default && (
            <span className="text-[10px] uppercase tracking-[0.15em] text-red-400 font-semibold">default</span>
          )}
        </div>
        <a
          href={offer.base_url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs font-mono text-zinc-500 hover:text-red-400 truncate block"
        >
          {offer.base_url.replace(/^https?:\/\//, "")}
        </a>
      </div>
      <div className="flex items-center gap-1.5">
        <Button
          variant="ghost"
          size="sm"
          onClick={onEdit}
          className="text-zinc-400 hover:text-white hover:bg-white/5"
          data-testid={`offer-edit-${offer.offer_id}`}
        >
          <Pencil className="w-4 h-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={remove}
          disabled={busy}
          className="text-zinc-500 hover:text-red-400 hover:bg-red-500/10"
          data-testid={`offer-delete-${offer.offer_id}`}
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

function OfferDialog({ mode, offer, open, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: offer?.name || "",
    base_url: offer?.base_url || "",
    utm_source: offer?.utm_source || "youtube",
    utm_medium: offer?.utm_medium || "video",
    utm_content_template: offer?.utm_content_template || "{video_title}",
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.name.trim() || !form.base_url.trim()) {
      toast.error("Name and Base URL are required");
      return;
    }
    setSaving(true);
    try {
      if (mode === "new") {
        await axios.post(`${API}/offers`, form);
        toast.success("Offer created");
      } else {
        await axios.patch(`${API}/offers/${offer.offer_id}`, form);
        toast.success("Offer updated");
      }
      onSaved();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : detail?.message;
      toast.error(msg || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="bg-zinc-950 border border-white/10 text-zinc-100 sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-white">{mode === "new" ? "New offer" : "Edit offer"}</DialogTitle>
          <DialogDescription className="text-zinc-400">
            A landing page you promote on YouTube. The base URL is what short links redirect to.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 mt-1">
          <Field label="Name">
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Course launch"
              className="bg-black/40 border-white/10 text-white"
              data-testid="offer-form-name"
            />
          </Field>
          <Field label="Base URL">
            <Input
              value={form.base_url}
              onChange={(e) => setForm({ ...form, base_url: e.target.value })}
              placeholder="https://launch.example.com"
              className="bg-black/40 border-white/10 text-white font-mono text-sm"
              data-testid="offer-form-baseurl"
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="utm_source">
              <Input
                value={form.utm_source}
                onChange={(e) => setForm({ ...form, utm_source: e.target.value })}
                className="bg-black/40 border-white/10 text-white font-mono text-sm"
                data-testid="offer-form-source"
              />
            </Field>
            <Field label="utm_medium">
              <Input
                value={form.utm_medium}
                onChange={(e) => setForm({ ...form, utm_medium: e.target.value })}
                className="bg-black/40 border-white/10 text-white font-mono text-sm"
                data-testid="offer-form-medium"
              />
            </Field>
          </div>
          <Field label="utm_content template" hint="Tokens: {video_title} (auto-slugified), {video_id}">
            <Input
              value={form.utm_content_template}
              onChange={(e) => setForm({ ...form, utm_content_template: e.target.value })}
              className="bg-black/40 border-white/10 text-white font-mono text-sm"
              data-testid="offer-form-template"
            />
          </Field>
        </div>
        <div className="flex justify-end gap-2 pt-3">
          <Button variant="ghost" onClick={onClose} className="text-zinc-400 hover:text-white">
            <X className="w-4 h-4 mr-1" /> Cancel
          </Button>
          <Button
            onClick={save}
            disabled={saving}
            className="bg-red-600 hover:bg-red-500 text-white border border-red-400/30"
            data-testid="offer-form-save"
          >
            {saving ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Saving</> : <><Check className="w-4 h-4 mr-1.5" /> Save</>}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <Label className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 font-semibold">{label}</Label>
      <div className="mt-1.5">{children}</div>
      {hint && <p className="text-[11px] text-zinc-600 mt-1">{hint}</p>}
    </div>
  );
}
